import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * DialogContent — endurecido para formularios de captura.
 *
 * Por defecto BLOQUEA el cierre por:
 *   - click en el overlay exterior (onPointerDownOutside / onInteractOutside)
 *   - tecla Escape (onEscapeKeyDown)
 *
 * Esto previene pérdida accidental de datos durante la captura de expedientes,
 * clientes, entidades y demás formularios sensibles del sistema RegTech.
 *
 * Opt-out: pasar `allowOutsideClose` (boolean o "escape" | "pointer") para
 * habilitar de nuevo el comportamiento nativo de Radix.
 *
 * Cierre siempre disponible vía botón "X" (arriba-derecha) o cualquier
 * <DialogClose /> incluido por el consumidor.
 */
const DialogContent = React.forwardRef(({
  className,
  children,
  allowOutsideClose = false,
  onPointerDownOutside,
  onInteractOutside,
  onEscapeKeyDown,
  ...props
}, ref) => {
  const allowPointer = allowOutsideClose === true || allowOutsideClose === "pointer";
  const allowEscape = allowOutsideClose === true || allowOutsideClose === "escape";

  const handlePointerDownOutside = (e) => {
    if (!allowPointer) e.preventDefault();
    onPointerDownOutside?.(e);
  };
  const handleInteractOutside = (e) => {
    if (!allowPointer) e.preventDefault();
    onInteractOutside?.(e);
  };
  const handleEscapeKeyDown = (e) => {
    if (!allowEscape) e.preventDefault();
    onEscapeKeyDown?.(e);
  };

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // max-h + overflow-y-auto → si el contenido excede la ventana, scrollea DENTRO del
          // modal. `calc(100vh-2rem)` deja 1rem de margen arriba/abajo. Se puede sobreescribir
          // vía `className` del consumidor (ej. command.jsx usa `overflow-hidden`).
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg max-h-[calc(100vh-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        onPointerDownOutside={handlePointerDownOutside}
        onInteractOutside={handleInteractOutside}
        onEscapeKeyDown={handleEscapeKeyDown}
        {...props}>
        {/* Botón X SIEMPRE visible: `sticky top-0` lo mantiene pegado a la esquina top-right
            del modal aunque el usuario haga scroll (patrón shadcn "sticky close"). El
            `-mb-8`+`-mr-2`+`-mt-2` compensa la celda del grid para que quede visualmente en el
            mismo corner que estaba antes con `absolute top-4 right-4`. El fondo semitransparente
            + backdrop-blur evita que la X se pierda encima del contenido al scrollear. */}
        <DialogPrimitive.Close
          data-testid="dialog-close-x"
          aria-label="Cerrar"
          className="sticky top-0 z-10 -mb-8 -mr-2 -mt-2 ml-auto flex h-8 w-8 items-center justify-center rounded-sm bg-background/80 opacity-70 ring-offset-background backdrop-blur-sm transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Cerrar</span>
        </DialogPrimitive.Close>
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
